import { Search, SentimentDissatisfied } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { config } from "../App";
import Cart, { generateCartItemsFrom } from "./Cart";
import Footer from "./Footer";
import Header from "./Header";
import ProductCard from "./ProductCard";
import "./Products.css";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 *
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 *
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

const Products = () => {
  const { enqueueSnackbar } = useSnackbar();
  const token = localStorage.getItem("token");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  /**
   * Fetch all products - GET /products
   *
   * @returns { Array.<Product> }
   */
  const performAPICall = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${config.endpoint}/products`);
      setProducts(res.data);
      setFilteredProducts(res.data);
      return res.data;
    } catch (e) {
      if (e.response && e.response.status === 500) {
        enqueueSnackbar(e.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Something went wrong. Check that the backend is running, reachable and returns valid JSON.",
          { variant: "error" }
        );
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Search products - GET /products/search?value=<search-query>
   *
   * @param {string} text
   * @returns { Array.<Product> }
   */
  const performSearch = async (text) => {
    try {
      const res = await axios.get(
        `${config.endpoint}/products/search?value=${text}`
      );
      setFilteredProducts(res.data);
      return res.data;
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setFilteredProducts([]);
      } else {
        enqueueSnackbar(
          "Something went wrong. Check that the backend is running, reachable and returns valid JSON.",
          { variant: "error" }
        );
      }
      return [];
    }
  };

  /**
   * Debounce handler - waits 500ms after the last keystroke before searching
   *
   * @param {{ target: { value: string } }} event
   * @param {NodeJS.Timeout} debounceTimeout
   */
  const debounceSearch = (event, debounceTimeout) => {
    const value = event.target.value;
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const timeout = setTimeout(() => {
      performSearch(value);
    }, 500);
    setDebounceTimeout(timeout);
  };

  /**
   * Fetch the logged in user's cart - GET /cart with Bearer token
   *
   * @param {string} token
   * @returns { Array.<{ productId: string, qty: number }> | null }
   */
  const fetchCart = async (token) => {
    if (!token) return;

    try {
      const res = await axios.get(`${config.endpoint}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (e) {
      if (e.response && e.response.status === 400) {
        enqueueSnackbar(e.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not fetch cart details. Check that the backend is running, reachable and returns valid JSON.",
          {
            variant: "error",
          }
        );
      }
      return null;
    }
  };

  /**
   * Return if a product already is present in the cart
   *
   * @param { Array.<{ productId: String, quantity: Number }> } items
   * @param { String } productId
   * @returns { Boolean }
   */
  const isItemInCart = (items, productId) => {
    return items.some((item) => item.productId === productId);
  };

  /**
   * Add or update a product in the cart - POST /cart with { productId, qty }
   *
   * @param {string} token
   * @param { Array.<{ productId: String, quantity: Number }> } items
   * @param { Array.<Product> } products
   * @param {string} productId
   * @param {number} qty
   * @param {{ preventDuplicate: boolean }} options
   *    preventDuplicate is true when triggered from the product card's "Add to Cart" button
   */
  const addToCart = async (
    token,
    items,
    products,
    productId,
    qty,
    options = { preventDuplicate: false }
  ) => {
    if (!token) {
      enqueueSnackbar("Login to add an item to the Cart", {
        variant: "warning",
      });
      return;
    }

    if (options.preventDuplicate && isItemInCart(items, productId)) {
      enqueueSnackbar(
        "Item already in cart. Use the cart sidebar to update quantity or remove item.",
        { variant: "warning" }
      );
      return;
    }

    try {
      const res = await axios.post(
        `${config.endpoint}/cart`,
        { productId, qty },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(generateCartItemsFrom(res.data, products));
    } catch (e) {
      if (e.response) {
        enqueueSnackbar(e.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not update the cart. Check that the backend is running, reachable and returns valid JSON.",
          { variant: "error" }
        );
      }
    }
  };

  useEffect(() => {
    const onLoadHandler = async () => {
      const productsData = await performAPICall();
      const cartData = await fetchCart(token);
      setItems(generateCartItemsFrom(cartData, productsData));
    };
    onLoadHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchInputProps = {
    endAdornment: (
      <InputAdornment position="end">
        <Search color="primary" />
      </InputAdornment>
    ),
  };

  const productsView = isLoading ? (
    <Box className="loading">
      <CircularProgress />
      <h4>Loading Products...</h4>
    </Box>
  ) : filteredProducts.length ? (
    <Grid container spacing={2} padding="1rem">
      {filteredProducts.map((product) => (
        <Grid item xs={6} md={3} key={product._id}>
          <ProductCard
            product={product}
            handleAddToCart={() =>
              addToCart(token, items, products, product._id, 1, {
                preventDuplicate: true,
              })
            }
          />
        </Grid>
      ))}
    </Grid>
  ) : (
    <Box className="loading">
      <SentimentDissatisfied color="action" />
      <h4>No products found</h4>
    </Box>
  );

  return (
    <div>
      <Header>
        <TextField
          className="search-desktop"
          size="small"
          InputProps={searchInputProps}
          placeholder="Search for items/categories"
          name="search"
          onChange={(e) => debounceSearch(e, debounceTimeout)}
        />
      </Header>

      {/* Search view for mobiles */}
      <TextField
        className="search-mobile"
        size="small"
        fullWidth
        InputProps={searchInputProps}
        placeholder="Search for items/categories"
        name="search"
        onChange={(e) => debounceSearch(e, debounceTimeout)}
      />
      <Grid container>
        <Grid item xs={12} md={token ? 9 : 12}>
          <Grid container>
            <Grid item className="product-grid">
              <Box className="hero">
                <p className="hero-heading">
                  India’s <span className="hero-highlight">FASTEST DELIVERY</span>{" "}
                  to your door step
                </p>
              </Box>
            </Grid>
          </Grid>
          {productsView}
        </Grid>
        {token && (
          <Grid item xs={12} md={3} bgcolor="#E9F5E1">
            <Cart
              products={products}
              items={items}
              handleQuantity={(productId, qty) =>
                addToCart(token, items, products, productId, qty)
              }
            />
          </Grid>
        )}
      </Grid>
      <Footer />
    </div>
  );
};

export default Products;
